import { FC, useEffect, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { ImageUploadBox } from '@service/seller/components/ImageUploadBox';
import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import { deliveryZoneError } from '@shared/api/giftDeliveryZone/giftDeliveryZone.admin';
import {
  giftItemBrandDataAtomFamily,
  giftItemBrandListDataAtom,
  giftItemBrandStatusAtomFamily,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import {
  createGiftItemBrand,
  updateGiftItemBrand,
} from '@shared/api/giftItemBrand/giftItemBrand.controller';
import {
  giftItemBrandListUrl,
  giftItemBrandUrl,
  useGiftItemBrandHook,
} from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  DeliveryDetail,
  GiftDeliveryDatePolicy,
  GiftItemBrand,
  isThanksPrintBoxSet,
  SettlementInfo,
  SupplyDeliveryFeeMode,
  ThanksPrintBox,
  ThanksPrintConfig,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import styles from '../components/GiftBrandManagement.module.css';
import { GiftDeliveryDatePolicyEditor } from '../components/GiftDeliveryDatePolicyEditor';
import { GiftDeliveryPolicyEditor } from '../components/GiftDeliveryPolicyEditor';
import { GiftDeliveryZoneManager } from '../components/GiftDeliveryZoneManager';
import {
  ThanksPrintBoxEditor,
  ThanksPrintBoxKey,
} from '../components/ThanksPrintBoxEditor';
import {
  datePolicyToForm,
  formToDatePolicy,
  validateDatePolicyForm,
} from '../utils/giftDeliveryDatePolicyForm';
import { formToPolicy, policyToForm } from '../utils/giftDeliveryPolicyForm';

/** 배송정보 폼이 직접 편집하는 deliveryDetail 필드 — 저장 시 이 키들만 폼 값으로 덮어쓴다 */
const FORM_MANAGED_DELIVERY_DETAIL_KEYS: readonly (keyof DeliveryDetail)[] = [
  'shippingInfo',
  'shippingFeeInfo',
  'shippingFee',
  'freeShippingMinQuantity',
  'notifyPhoneNumbers',
];

/**
 * 기존 deliveryDetail에서 일반 입력 필드 외의 값을 보존한다. zonePolicy는 별도 편집기로 덮어쓴다.
 * 통째 교체 PATCH에 spread로 실어 보존하기 위한 것 — 값이 없는(undefined/null) 키는 제외.
 */
const pickPreservedDeliveryDetail = (
  existing: DeliveryDetail | undefined,
): Partial<DeliveryDetail> => {
  if (!existing) return {};
  const managed: readonly string[] = FORM_MANAGED_DELIVERY_DETAIL_KEYS;
  const preserved: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(existing)) {
    if (managed.includes(key) || value == null) continue;
    preserved[key] = value;
  }
  return preserved as Partial<DeliveryDetail>;
};

/**
 * 배송 희망일 정책을 켜서 보냈는데 응답에 없으면 서버가 아직 그 필드를 모르는 것(스키마 밖 필드는
 * 조용히 버려진다) — 저장된 줄 알고 넘어가지 않게 알린다. 백엔드 반영 전까지의 안전장치.
 */
const datePolicyDroppedNotice = (
  sent: GiftDeliveryDatePolicy | undefined,
  saved: GiftItemBrand,
): string =>
  sent?.enabled && !saved.deliveryDetail?.datePolicy
    ? '\n\n※ 배송 희망일 지정은 서버가 아직 지원하지 않아 저장되지 않았습니다.'
    : '';

/** 폼 내부에서 다루는 감사인쇄 템플릿 (신규 파일 업로드 상태 포함) */
interface TemplateForm {
  key: string; // 렌더 안정용 로컬 키
  _id?: string; // 기존 템플릿 id (신규는 없음)
  name: string;
  templateImage: Image | null; // 기존 populated 이미지
  file: File | null; // 새로 선택한 파일
  preview: string; // 새 파일 object URL
  // 감사글 지정 문구 세트 — 등록 시 구매자는 이 중 하나만 선택 (자유입력 불가).
  // 줄바꿈·들여쓰기까지 인쇄 원본이므로 trim하지 않고 그대로 보존한다.
  messagePresets: string[];
  senderNameMaxLength: string; // 보내는 분 인쇄문구("이름 배상" 전체) 글자수 제한
  fontScale: string; // 글자 크기 (이미지 세로높이 대비 %, 예: "5" = 0.05)
  messageBox?: ThanksPrintBox;
  senderBox?: ThanksPrintBox;
  enabled: boolean;
}

const genKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tpl-${Math.random().toString(36).slice(2)}`;

const createEmptyTemplate = (): TemplateForm => ({
  key: genKey(),
  name: '',
  templateImage: null,
  file: null,
  preview: '',
  messagePresets: [],
  senderNameMaxLength: '',
  fontScale: '',
  enabled: true,
});

type FormSectionKey = 'coupon' | 'delivery' | 'settlement' | 'thanks';

/** 처음에는 기본정보에 집중하고, 목록에서 선택한 유형의 설정만 펼친다. */
const initialCollapsed = (typeHint: string | null): Record<FormSectionKey, boolean> => ({
  coupon: typeHint !== 'coupon',
  delivery: typeHint !== 'delivery',
  settlement: true,
  thanks: true,
});

/** 선택 설정은 제목 전체를 눌러 펼친다. */
const SectionTitle: FC<{
  title: string;
  description: string;
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
}> = ({ title, description, collapsed, onToggle, className = '' }) => (
  <h2 className="min-w-0 flex-1">
    <button
      type="button"
      aria-expanded={!collapsed}
      onClick={onToggle}
      className={`flex w-full items-start gap-3 rounded text-left focus-visible:outline-indigo-600 ${className}`}
    >
      <ChevronDownIcon
        aria-hidden
        className={`mt-0.5 h-5 w-5 shrink-0 text-gray-400 transition-transform ${
          collapsed ? '-rotate-90' : ''
        }`}
      />
      <span>
        <span className="block text-base font-semibold text-gray-900">{title}</span>
        <span className="mt-1 block text-sm font-normal text-gray-500">
          {description}
        </span>
      </span>
    </button>
  </h2>
);

export const GiftItemBrandFormPage: FC = function GiftItemBrandFormPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { sellerRole, isAdmin } = useAuth();

  const isEditMode = Boolean(brandId);

  // 목록에서 배송형/쿠폰형 필터로 진입하면 반대 유형 섹션을 접어서 연다
  const [searchParams] = useSearchParams();
  const typeHint = searchParams.get('type');
  const [collapsed, setCollapsed] = useState(() => initialCollapsed(typeHint));
  const toggleSection = (key: FormSectionKey) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  // 정산정보는 플랫폼↔공급사 정산용이라 관리자만 조회/수정
  const canEditSettlement = isAdmin();

  // 수정 모드일 때 데이터 조회
  useGiftItemBrandHook(brandId ?? '', sellerRole(), !isEditMode);
  const status = useRecoilValue(giftItemBrandStatusAtomFamily(brandId ?? ''));
  const brandData = useRecoilValue(giftItemBrandDataAtomFamily(brandId ?? ''));

  // 목록 데이터 업데이트용
  const setListData = useSetRecoilState(giftItemBrandListDataAtom);
  const queryClient = useQueryClient();

  // 폼 상태
  const [name, setName] = useState('');
  const [validPeriod, setValidPeriod] = useState('');
  const [placeOfExchange, setPlaceOfExchange] = useState('');
  const [zonePolicyForm, setZonePolicyForm] = useState(() => policyToForm());
  // 배송 희망일 지정 정책 (deliveryDetail.datePolicy) — 관리자만 편집
  const [datePolicyForm, setDatePolicyForm] = useState(() => datePolicyToForm());
  const [shippingInfo, setShippingInfo] = useState('');
  const [shippingFeeInfo, setShippingFeeInfo] = useState('');
  const [shippingFee, setShippingFee] = useState(''); // 배송비 (원)
  const [freeShippingMinQuantity, setFreeShippingMinQuantity] = useState(''); // 무료배송 최소수량
  const [notifyPhoneNumbers, setNotifyPhoneNumbers] = useState<string[]>(['']);
  // 개별배송 허용 여부 (false면 일괄배송 전용). 상품에 설정이 없으면 이 값을 따름
  const [allowIndividualDelivery, setAllowIndividualDelivery] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 정산정보(settlementInfo) 상태 — 관리자 전용
  const [settlementAccountEntity, setSettlementAccountEntity] = useState<AccountEntity>(
    AccountEntity.NotDecided,
  );
  const [settlementDeliveryFeeMode, setSettlementDeliveryFeeMode] =
    useState<SupplyDeliveryFeeMode>('included');
  const [settlementShippingFee, setSettlementShippingFee] = useState('');
  const [settlementFreeShippingMinQuantity, setSettlementFreeShippingMinQuantity] =
    useState('');
  const [settlementCourierFee, setSettlementCourierFee] = useState('');
  const [settlementCourierFreeMinQuantity, setSettlementCourierFreeMinQuantity] =
    useState('');
  const [settlementDay, setSettlementDay] = useState('');

  // 감사메시지 인쇄(thanksPrint) 상태
  const [thanksEnabled, setThanksEnabled] = useState(false);
  const [thanksGuideText, setThanksGuideText] = useState('');
  const [templates, setTemplates] = useState<TemplateForm[]>([]);

  // 컴포넌트 언마운트 시 생성한 object URL 정리
  useEffect(() => {
    return () => {
      templates.forEach((t) => t.preview && URL.revokeObjectURL(t.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 템플릿 부분 업데이트 헬퍼
  const updateTemplate = (key: string, patch: Partial<TemplateForm>) => {
    setTemplates((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  };

  // 지정 문구 세트 편집 헬퍼
  const updatePreset = (key: string, idx: number, value: string) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.key === key
          ? {
              ...t,
              messagePresets: t.messagePresets.map((p, i) => (i === idx ? value : p)),
            }
          : t,
      ),
    );
  };
  const addPreset = (key: string) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.key === key ? { ...t, messagePresets: [...t.messagePresets, ''] } : t,
      ),
    );
  };
  const removePreset = (key: string, idx: number) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.key === key
          ? { ...t, messagePresets: t.messagePresets.filter((_, i) => i !== idx) }
          : t,
      ),
    );
  };

  // 템플릿 이미지 파일 선택 (object URL 미리보기 생성)
  const handleTemplateFile = (key: string, file: File) => {
    const url = URL.createObjectURL(file);
    setTemplates((prev) =>
      prev.map((t) => {
        if (t.key !== key) return t;
        if (t.preview) URL.revokeObjectURL(t.preview);
        return { ...t, file, preview: url };
      }),
    );
  };

  // 박스 좌표 변경 (undefined = 영역 제거 — 감사글 영역을 지우면 이름만 인쇄하는 카드)
  const handleBoxChange = (
    key: string,
    boxKey: ThanksPrintBoxKey,
    box: ThanksPrintBox | undefined,
  ) => {
    updateTemplate(key, boxKey === 'message' ? { messageBox: box } : { senderBox: box });
  };

  const addTemplate = () => setTemplates((prev) => [...prev, createEmptyTemplate()]);

  const removeTemplate = (key: string) => {
    setTemplates((prev) => {
      const target = prev.find((t) => t.key === key);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((t) => t.key !== key);
    });
  };

  // 에디터에 표시할 템플릿 이미지 URL (새 파일 우선, 없으면 기존)
  const templateDisplayUrl = (t: TemplateForm) =>
    t.preview || (t.templateImage ? fileBucketUrl(t.templateImage.url, 'raw') : '');

  // 수정 모드일 때 기존 데이터 로드 — 최초 1회만 폼을 채운다.
  // 창 포커스 등으로 react-query가 refetch하면(refetchOnWindowFocus 기본 true) 원자
  // 참조가 바뀌어 이 effect가 다시 돌지만, 이미 초기화됐다면 입력 중인 값을 덮어쓰지
  // 않는다. (저장 후에는 목록으로 navigate하므로 in-place 재반영은 불필요.)
  const didInitRef = useRef(false);
  useEffect(() => {
    if (isEditMode && brandData && !didInitRef.current) {
      didInitRef.current = true;
      setName(brandData.name);
      setValidPeriod(brandData.couponDetail?.validPeriod ?? '');
      setPlaceOfExchange(brandData.couponDetail?.placeOfExchange ?? '');
      setZonePolicyForm(policyToForm(brandData.deliveryDetail?.zonePolicy));
      setDatePolicyForm(datePolicyToForm(brandData.deliveryDetail?.datePolicy));
      setShippingInfo(brandData.deliveryDetail?.shippingInfo ?? '');
      setShippingFeeInfo(brandData.deliveryDetail?.shippingFeeInfo ?? '');
      setShippingFee(brandData.deliveryDetail?.shippingFee?.toString() ?? '');
      setFreeShippingMinQuantity(
        brandData.deliveryDetail?.freeShippingMinQuantity?.toString() ?? '',
      );
      const phones = brandData.deliveryDetail?.notifyPhoneNumbers;
      setNotifyPhoneNumbers(phones && phones.length > 0 ? [...phones] : ['']);
      setAllowIndividualDelivery(brandData.allowIndividualDelivery ?? true);

      const si = brandData.settlementInfo;
      setSettlementAccountEntity(si?.accountEntity ?? AccountEntity.NotDecided);
      setSettlementDeliveryFeeMode(
        si?.deliveryFeeMode ?? ((si?.courierFee ?? 0) > 0 ? 'quantity' : 'included'),
      );
      setSettlementShippingFee(si?.shippingFee?.toString() ?? '');
      setSettlementFreeShippingMinQuantity(si?.freeShippingMinQuantity?.toString() ?? '');
      setSettlementCourierFee(si?.courierFee?.toString() ?? '');
      setSettlementCourierFreeMinQuantity(si?.courierFreeMinQuantity?.toString() ?? '');
      setSettlementDay(si?.settlementDay?.toString() ?? '');

      const tp = brandData.thanksPrint;
      setThanksEnabled(tp?.enabled ?? false);
      setThanksGuideText(tp?.guideText ?? '');
      const loaded = (tp?.templates ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map<TemplateForm>((t) => ({
          key: t._id ?? genKey(),
          _id: t._id,
          name: t.name ?? '',
          templateImage: t.templateImage ?? null,
          file: null,
          preview: '',
          messagePresets: [...(t.messagePresets ?? [])],
          senderNameMaxLength: t.senderNameMaxLength?.toString() ?? '',
          // 0~1 배율 → % 표기 (예: 0.05 → "5")
          fontScale:
            t.fontScale != null ? (Math.round(t.fontScale * 1000) / 10).toString() : '',
          // 좌표 없는 유령 박스({align:'center'}만 남은 것)는 미설정으로 정리
          messageBox: isThanksPrintBoxSet(t.messageBox) ? t.messageBox : undefined,
          senderBox: isThanksPrintBoxSet(t.senderBox) ? t.senderBox : undefined,
          enabled: t.enabled ?? true,
        }));
      setTemplates(loaded);

      // 수정 모드: 값이 이미 입력된 섹션은 유형 필터와 무관하게 펼친다
      const dd = brandData.deliveryDetail;
      const hasCoupon = Boolean(
        brandData.couponDetail?.validPeriod || brandData.couponDetail?.placeOfExchange,
      );
      const hasDelivery = Boolean(
        dd?.zonePolicy ||
          dd?.datePolicy ||
          dd?.shippingInfo ||
          dd?.shippingFeeInfo ||
          dd?.shippingFee != null ||
          dd?.freeShippingMinQuantity != null ||
          (dd?.notifyPhoneNumbers && dd.notifyPhoneNumbers.length > 0) ||
          brandData.allowIndividualDelivery === false,
      );
      const hasSettlement = Boolean(
        si &&
          ((si.accountEntity && si.accountEntity !== AccountEntity.NotDecided) ||
            si.shippingFee != null ||
            si.freeShippingMinQuantity != null ||
            si.deliveryFeeMode != null ||
            si.courierFee != null ||
            si.courierFreeMinQuantity != null ||
            si.settlementDay != null),
      );
      const hasThanks = Boolean(tp?.enabled || (tp?.templates?.length ?? 0) > 0);
      setCollapsed((prev) => ({
        coupon: prev.coupon && !hasCoupon,
        delivery: prev.delivery && !hasDelivery,
        settlement: prev.settlement && !hasSettlement,
        thanks: prev.thanks && !hasThanks,
      }));
    }
  }, [isEditMode, brandData]);

  // 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!name.trim()) {
      alert('브랜드명을 입력해주세요.');
      return;
    }

    if (thanksEnabled) {
      if (templates.length === 0) {
        alert('감사인쇄 사용 시 템플릿을 1개 이상 추가해주세요.');
        return;
      }
      if (templates.some((t) => !t.file && !t.templateImage)) {
        alert('모든 감사인쇄 템플릿에 이미지를 등록해주세요.');
        return;
      }
    }

    if (canEditSettlement) {
      const datePolicyError = validateDatePolicyForm(datePolicyForm);
      if (datePolicyError) {
        alert(datePolicyError);
        return;
      }
    }

    try {
      setIsSubmitting(true);

      const couponDetail =
        validPeriod.trim() || placeOfExchange.trim()
          ? {
              ...(validPeriod.trim() && { validPeriod: validPeriod.trim() }),
              ...(placeOfExchange.trim() && { placeOfExchange: placeOfExchange.trim() }),
            }
          : undefined;

      const filteredPhoneNumbers = notifyPhoneNumbers
        .map((p) => p.trim())
        .filter(Boolean);

      const zonePolicy = canEditSettlement
        ? formToPolicy(zonePolicyForm, brandData?.deliveryDetail?.zonePolicy)
        : brandData?.deliveryDetail?.zonePolicy;
      // 배송 희망일 정책 — 관리자만 편집, 그 외 역할은 기존 값 그대로
      const datePolicy = canEditSettlement
        ? formToDatePolicy(datePolicyForm, brandData?.deliveryDetail?.datePolicy)
        : brandData?.deliveryDetail?.datePolicy;
      const hasDeliveryDetail =
        zonePolicy ||
        datePolicy ||
        shippingInfo.trim() ||
        shippingFeeInfo.trim() ||
        shippingFee.trim() !== '' ||
        freeShippingMinQuantity.trim() !== '' ||
        filteredPhoneNumbers.length > 0;

      // deliveryDetail은 통째 교체되므로 알려지지 않은 필드도 보존한다.
      // zonePolicy는 별도 편집기로 갱신하며 allowedPaymentMethods 등 기존 부가 설정은 유지한다.
      const preservedDeliveryDetail = pickPreservedDeliveryDetail(
        isEditMode ? brandData?.deliveryDetail : undefined,
      );
      const hasPreservedDeliveryDetail = Object.keys(preservedDeliveryDetail).length > 0;

      const deliveryDetail: DeliveryDetail | undefined =
        hasDeliveryDetail || hasPreservedDeliveryDetail
          ? {
              ...preservedDeliveryDetail,
              ...(zonePolicy && { zonePolicy }),
              ...(datePolicy && { datePolicy }),
              ...(shippingInfo.trim() && { shippingInfo: shippingInfo.trim() }),
              ...(shippingFeeInfo.trim() && { shippingFeeInfo: shippingFeeInfo.trim() }),
              ...(zonePolicy?.enabled
                ? { shippingFee: 0 }
                : shippingFee.trim() !== ''
                ? { shippingFee: Number(shippingFee) }
                : {}),
              ...(freeShippingMinQuantity.trim() !== '' && {
                freeShippingMinQuantity: Number(freeShippingMinQuantity),
              }),
              ...(filteredPhoneNumbers.length > 0 && {
                notifyPhoneNumbers: filteredPhoneNumbers,
              }),
            }
          : undefined;

      // 감사인쇄 설정 구성 — 신규 파일은 업로드 후 id로 변환, order는 화면 순서대로
      const builtTemplates: ThanksPrintTemplate<string>[] = [];
      for (let i = 0; i < templates.length; i++) {
        const t = templates[i];
        if (!t) continue;
        let imageId = t.templateImage?._id;
        if (t.file) {
          const uploaded = await postImage(t.file, sellerRole());
          imageId = uploaded._id;
        }
        // 지정 문구: 완전히 빈 항목만 제거 — 내부 줄바꿈·들여쓰기는 인쇄 원본이라 보존
        const presets = t.messagePresets.filter((p) => p.trim().length > 0);
        const senderNameMaxLength = Number(t.senderNameMaxLength);
        const fontScalePercent = Number(t.fontScale);
        builtTemplates.push({
          ...(t._id && { _id: t._id }),
          ...(t.name.trim() && { name: t.name.trim() }),
          ...(imageId && { templateImage: imageId }),
          ...(presets.length > 0 && { messagePresets: presets }),
          ...(t.senderNameMaxLength.trim() !== '' &&
            senderNameMaxLength > 0 && { senderNameMaxLength }),
          // % 표기 → 0~1 배율 (예: "5" → 0.05)
          ...(t.fontScale.trim() !== '' &&
            fontScalePercent > 0 && { fontScale: fontScalePercent / 100 }),
          ...(isThanksPrintBoxSet(t.messageBox) && { messageBox: t.messageBox }),
          ...(isThanksPrintBoxSet(t.senderBox) && { senderBox: t.senderBox }),
          order: i,
          enabled: t.enabled,
        });
      }

      const thanksPrint: ThanksPrintConfig<string> = {
        enabled: thanksEnabled,
        templates: builtTemplates,
      };
      if (thanksGuideText.trim()) thanksPrint.guideText = thanksGuideText.trim();

      // 정산정보 — 관리자만 전송 (비관리자는 필드 자체를 payload에서 제외해 기존 값 보존)
      const settlementInfo: SettlementInfo | undefined = canEditSettlement
        ? {
            accountEntity: settlementAccountEntity,
            deliveryFeeMode: settlementDeliveryFeeMode,
            ...(settlementShippingFee.trim() !== '' && {
              shippingFee: Number(settlementShippingFee),
            }),
            ...(settlementFreeShippingMinQuantity.trim() !== '' && {
              freeShippingMinQuantity: Number(settlementFreeShippingMinQuantity),
            }),
            ...(settlementCourierFee.trim() !== '' && {
              courierFee: Number(settlementCourierFee),
            }),
            ...(settlementCourierFreeMinQuantity.trim() !== '' && {
              courierFreeMinQuantity: Number(settlementCourierFreeMinQuantity),
            }),
            ...(settlementDay.trim() !== '' && {
              settlementDay: Number(settlementDay),
            }),
          }
        : undefined;

      const payload = {
        name,
        couponDetail,
        deliveryDetail,
        allowIndividualDelivery: zonePolicy?.enabled ? false : allowIndividualDelivery,
        thanksPrint,
        ...(canEditSettlement && { settlementInfo }),
      };

      if (isEditMode && brandId) {
        const updated = await updateGiftItemBrand(brandId, payload, sellerRole());
        setListData((prev) =>
          prev.map((item) => (item._id === brandId ? updated : item)),
        );
        // PATCH 응답은 populate 미적용 → 캐시 무효화로 최신 populated 데이터를 재조회
        // (단건 캐시까지 무효화해야 재진입 시 폼이 옛 값으로 다시 로드되지 않음)
        await queryClient.invalidateQueries(giftItemBrandUrl(brandId, sellerRole()));
        await queryClient.invalidateQueries(giftItemBrandListUrl(sellerRole()));
        alert(`수정되었습니다.${datePolicyDroppedNotice(datePolicy, updated)}`);
      } else {
        const created = await createGiftItemBrand(payload, sellerRole());
        setListData((prev) => [...prev, created]);
        await queryClient.invalidateQueries(giftItemBrandListUrl(sellerRole()));
        alert(`등록되었습니다.${datePolicyDroppedNotice(datePolicy, created)}`);
      }

      navigate('/gift-seller/item-brand');
    } catch (error) {
      console.error(error);
      alert(deliveryZoneError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 중 (수정 모드)
  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  if (isEditMode && (status === 'error' || (status === 'success' && !brandData))) {
    return (
      <div role="alert" className="min-h-screen bg-gray-50 px-6 py-20 text-center">
        <h1 className="text-lg font-semibold">브랜드 정보를 불러오지 못했습니다.</h1>
        <p className="mt-2 text-sm text-gray-500">
          다시 시도하거나 목록에서 브랜드를 확인해주세요.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/gift-seller/brand')}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm"
          >
            목록으로
          </button>
          <button
            type="button"
            onClick={() =>
              queryClient.invalidateQueries(giftItemBrandUrl(brandId ?? '', sellerRole()))
            }
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-6xl">
        <header className="mb-7">
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => navigate('/gift-seller/brand')}
            className="mb-4 inline-flex items-center gap-1.5 rounded text-sm text-gray-500 hover:text-gray-900 disabled:opacity-40"
          >
            <ArrowLeftIcon aria-hidden className="h-4 w-4" />
            브랜드 목록
          </button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isEditMode ? '브랜드 수정' : '새 브랜드 등록'}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {isEditMode
              ? '브랜드 정보와 운영 설정을 수정합니다.'
              : '브랜드명을 입력하고, 운영에 필요한 설정을 추가하세요.'}
          </p>
        </header>
        <div className="grid items-start gap-6 xl:grid-cols-[180px_minmax(0,1fr)]">
          <nav
            aria-label="브랜드 설정 바로가기"
            className="flex flex-wrap gap-1 xl:sticky xl:top-6 xl:flex-col"
          >
            <p className="mb-3 hidden px-3 text-xs font-medium text-gray-400 xl:block">
              설정 항목
            </p>
            <a
              href="#brand-basic"
              className="rounded px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
            >
              기본정보 <span className="ml-1 text-xs text-indigo-600">필수</span>
            </a>
            {(
              [
                { key: 'delivery', label: '배송 설정' },
                { key: 'coupon', label: '쿠폰 설정' },
                ...(canEditSettlement ? [{ key: 'settlement', label: '정산 설정' }] : []),
                { key: 'thanks', label: '감사인쇄' },
              ] as { key: FormSectionKey; label: string }[]
            ).map(({ key, label }) => (
              <a
                key={key}
                href={`#brand-${key}`}
                onClick={() => setCollapsed((prev) => ({ ...prev, [key]: false }))}
                className="rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              >
                {label}
              </a>
            ))}
          </nav>
          <form
            onSubmit={handleSubmit}
            className="min-w-0"
            aria-label={isEditMode ? '브랜드 수정' : '브랜드 등록'}
          >
            <fieldset disabled={isSubmitting} className="min-w-0">
              <section
                id="brand-basic"
                className="scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
                aria-labelledby="brand-basic-heading"
              >
                <div className="mb-5 flex items-center gap-2">
                  <h2 id="brand-basic-heading" className="text-base font-semibold">
                    기본정보
                  </h2>
                  <span className="text-xs text-indigo-600">필수</span>
                </div>
                <label
                  htmlFor="brand-name"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  브랜드명 <span className="text-indigo-600">*</span>
                </label>
                <input
                  id="brand-name"
                  name="brandName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="예: 스타벅스, 투썸플레이스"
                  required
                  aria-describedby="brand-name-help"
                  className="w-full rounded border border-gray-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <p id="brand-name-help" className="mt-2 text-xs text-gray-500">
                  상품과 관리 목록에 표시되는 이름입니다.
                </p>
              </section>

              <section
                id="brand-delivery"
                className="mt-4 scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <SectionTitle
                  title="배송 설정"
                  description="배송 안내, 추가금, 알림 수신번호"
                  collapsed={collapsed.delivery}
                  onToggle={() => toggleSection('delivery')}
                />
                {!collapsed.delivery && (
                  <div className="mt-3 space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        배송정보/기간 안내
                      </label>
                      <textarea
                        value={shippingInfo}
                        onChange={(e) => setShippingInfo(e.target.value)}
                        placeholder="예: 주문 후 2~3일 이내 출고"
                        className="h-20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        배송비 안내
                      </label>
                      <textarea
                        value={shippingFeeInfo}
                        onChange={(e) => setShippingFeeInfo(e.target.value)}
                        placeholder="예: 무료배송 / 3,000원"
                        className="h-20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    {canEditSettlement && (
                      <>
                        <GiftDeliveryPolicyEditor
                          value={zonePolicyForm}
                          onChange={setZonePolicyForm}
                        />
                        {brandId ? (
                          <GiftDeliveryZoneManager
                            key={brandId}
                            brandId={brandId}
                            policyRevision={JSON.stringify(zonePolicyForm)}
                            getPolicy={() =>
                              formToPolicy(
                                zonePolicyForm,
                                brandData?.deliveryDetail?.zonePolicy,
                              )
                            }
                          />
                        ) : (
                          <p className="text-sm text-gray-500">
                            브랜드를 먼저 저장하면 배송지역 등록과 엑셀 업로드를 사용할 수
                            있습니다.
                          </p>
                        )}
                      </>
                    )}
                    {canEditSettlement && (
                      <GiftDeliveryDatePolicyEditor
                        value={datePolicyForm}
                        onChange={setDatePolicyForm}
                      />
                    )}
                    {!zonePolicyForm.enabled && (
                      <>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              소량주문 추가금 (개당)
                            </label>
                            <input
                              type="number"
                              value={shippingFee}
                              onChange={(e) => setShippingFee(e.target.value)}
                              min={0}
                              placeholder="예: 500"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              추가금 면제 최소수량
                            </label>
                            <input
                              type="number"
                              value={freeShippingMinQuantity}
                              onChange={(e) => setFreeShippingMinQuantity(e.target.value)}
                              min={0}
                              placeholder="예: 20"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-400">
                          이 수량 미만 주문 시 <b>개당</b> 추가금이 단가에
                          붙습니다(이상이면 면제). 상품에 별도 설정이 없으면 이 값을
                          따릅니다.
                        </p>
                      </>
                    )}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        배송 알림 수신 전화번호 (최대 3개)
                      </label>
                      <div className="space-y-2">
                        {notifyPhoneNumbers.map((phone, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="tel"
                              value={phone}
                              onChange={(e) => {
                                const updated = [...notifyPhoneNumbers];
                                updated[index] = e.target.value;
                                setNotifyPhoneNumbers(updated);
                              }}
                              placeholder="예: 01012345678"
                              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                            {notifyPhoneNumbers.length > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setNotifyPhoneNumbers(
                                    notifyPhoneNumbers.filter((_, i) => i !== index),
                                  )
                                }
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-red-300 text-red-500 hover:bg-red-50"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {notifyPhoneNumbers.length < 3 && (
                        <button
                          type="button"
                          onClick={() =>
                            setNotifyPhoneNumbers([...notifyPhoneNumbers, ''])
                          }
                          className="mt-2 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                        >
                          + 번호 추가
                        </button>
                      )}
                      <p className="mt-1 text-xs text-gray-400">
                        배송지 입력/변경 시 SMS 알림을 받을 전화번호를 입력하세요.
                      </p>
                    </div>
                    <div className="flex items-start justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          개별배송 허용
                        </label>
                        <p className="mt-0.5 text-xs text-gray-500">
                          끄면 이 브랜드 상품은 일괄배송(Bulk) 전용이 됩니다. 상품에 별도
                          설정이 없으면 이 값을 따릅니다.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={!zonePolicyForm.enabled && allowIndividualDelivery}
                        disabled={zonePolicyForm.enabled}
                        onChange={(e) => setAllowIndividualDelivery(e.target.checked)}
                        className="mt-0.5 h-5 w-5 accent-indigo-600"
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      배송형 상품의 배송정보를 입력해주세요. 이 브랜드의 모든 상품에 기본
                      적용됩니다.
                    </p>
                  </div>
                )}
              </section>

              <section
                id="brand-coupon"
                className="mt-4 scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <SectionTitle
                  title="쿠폰 설정"
                  description="유효기간과 교환처 · 쿠폰형 브랜드에 적용"
                  collapsed={collapsed.coupon}
                  onToggle={() => toggleSection('coupon')}
                />
                {!collapsed.coupon && (
                  <div className="mt-3 space-y-4">
                    <div>
                      <label
                        htmlFor="brand-valid-period"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        유효기간
                      </label>
                      <input
                        id="brand-valid-period"
                        value={validPeriod}
                        onChange={(e) => setValidPeriod(e.target.value)}
                        placeholder="예: 발행일로부터 93일"
                        className="w-full rounded border border-gray-300 px-3 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="brand-exchange"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        교환처
                      </label>
                      <input
                        id="brand-exchange"
                        value={placeOfExchange}
                        onChange={(e) => setPlaceOfExchange(e.target.value)}
                        placeholder="예: 전국 스타벅스 매장"
                        className="w-full rounded border border-gray-300 px-3 py-2.5 text-sm"
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      쿠폰형 상품의 유효기간과 교환처 정보를 입력해주세요. 입력하지 않아도
                      됩니다.
                    </p>
                  </div>
                )}
              </section>

              {/* 정산정보 (관리자 전용) */}
              {canEditSettlement && (
                <section
                  id="brand-settlement"
                  className="mt-4 scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
                >
                  <SectionTitle
                    title="정산 설정"
                    description="정산 주체와 공급사 단가 · 관리자 전용"
                    collapsed={collapsed.settlement}
                    onToggle={() => toggleSection('settlement')}
                  />
                  {!collapsed.settlement && (
                    <>
                      <p className="mb-3 mt-1 text-sm text-gray-500">
                        플랫폼↔공급사 정산용 단가입니다. 구매자에게 노출되지 않습니다.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            정산 주체
                          </label>
                          <select
                            value={settlementAccountEntity}
                            onChange={(e) =>
                              setSettlementAccountEntity(e.target.value as AccountEntity)
                            }
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          >
                            <option value={AccountEntity.NotDecided}>미정</option>
                            <option value={AccountEntity.Mari}>마리</option>
                            <option value={AccountEntity.Gaon}>가온</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-400">
                            이 공급사의 도매가를 정산하는 주체입니다.
                          </p>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              소량주문 추가단가 (개당)
                            </label>
                            <input
                              type="number"
                              value={settlementShippingFee}
                              onChange={(e) => setSettlementShippingFee(e.target.value)}
                              min={0}
                              placeholder="예: 1000"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              추가단가 면제 최소수량
                            </label>
                            <input
                              type="number"
                              value={settlementFreeShippingMinQuantity}
                              onChange={(e) =>
                                setSettlementFreeShippingMinQuantity(e.target.value)
                              }
                              min={0}
                              placeholder="예: 20"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-400">
                          주문 수량이 이 값 미만이면 <b>개당</b> 추가단가를 정산 때
                          청구합니다(이상이면 면제). 아래 배송비 정산 방식과 별개로
                          적용되는 상품 추가단가입니다.
                        </p>
                        <div>
                          <label
                            htmlFor="settlement-delivery-fee-mode"
                            className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            업체에 지급할 배송비
                          </label>
                          <select
                            id="settlement-delivery-fee-mode"
                            value={settlementDeliveryFeeMode}
                            onChange={(e) =>
                              setSettlementDeliveryFeeMode(
                                e.target.value as SupplyDeliveryFeeMode,
                              )
                            }
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          >
                            <option value="included">
                              별도 지급 없음 — 상품 단가에 포함
                            </option>
                            <option value="quantity">수량 기준 택배비 지급</option>
                            <option value="orderDeliveryFee">
                              고객이 결제한 배송비 전액 지급
                            </option>
                          </select>
                          <p className="mt-2 text-sm text-gray-600">
                            {settlementDeliveryFeeMode === 'orderDeliveryFee'
                              ? '주문에 저장된 기본배송비 + 지역 추가배송비 전액을 업체에 지급합니다. 수량 면제 없이 주문당 한 번 지급하며, 기존 건당 택배비는 더하지 않습니다.'
                              : settlementDeliveryFeeMode === 'included'
                              ? '배송비를 상품 단가에 포함한 계약입니다. 업체 정산에 별도 배송비를 더하지 않습니다.'
                              : '같은 송장으로 묶인 배송은 택배비를 한 번 지급하고, 합계수량이 면제 기준 이상이면 지급하지 않습니다.'}
                          </p>
                          {settlementDeliveryFeeMode === 'orderDeliveryFee' && (
                            <p className="mt-1 text-xs text-gray-500">
                              배송비가 없는 구주문은 0원입니다. 변경은 새 정산집계와 기존
                              집계 재계산에 적용되며 지급완료 내역은 바뀌지 않습니다.
                            </p>
                          )}
                        </div>
                        {settlementDeliveryFeeMode === 'quantity' && (
                          <>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                  택배비 (건당)
                                </label>
                                <input
                                  type="number"
                                  value={settlementCourierFee}
                                  onChange={(e) =>
                                    setSettlementCourierFee(e.target.value)
                                  }
                                  min={0}
                                  placeholder="예: 4500"
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                />
                              </div>
                              <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                  택배비 무료 최소수량
                                </label>
                                <input
                                  type="number"
                                  value={settlementCourierFreeMinQuantity}
                                  onChange={(e) =>
                                    setSettlementCourierFreeMinQuantity(e.target.value)
                                  }
                                  min={0}
                                  placeholder="예: 50"
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                />
                              </div>
                            </div>
                            <p className="text-xs text-gray-400">
                              주문 수량이 이 값 미만이면 <b>건당</b> 택배비를 정산 때
                              청구합니다(이상이면 무료). 면제수량을 비우거나 0으로
                              설정하면 수량 면제 없이 지급합니다.
                            </p>
                          </>
                        )}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            정산일 (익월 기준)
                          </label>
                          <input
                            type="number"
                            value={settlementDay}
                            onChange={(e) => setSettlementDay(e.target.value)}
                            min={1}
                            max={31}
                            placeholder="예: 15"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            익월 며칠에 정산할지 일자를 입력하세요. 예: 15 → 익월 15일
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* 감사메시지 인쇄 설정 */}
              <section
                id="brand-thanks"
                className="mt-4 scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <div className="flex items-center justify-between">
                  <SectionTitle
                    title="감사인쇄"
                    description="감사글과 보내는 분을 인쇄하는 브랜드에 적용"
                    collapsed={collapsed.thanks}
                    onToggle={() => toggleSection('thanks')}
                  />
                  <label className="flex shrink-0 cursor-pointer items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {thanksEnabled ? '사용' : '미사용'}
                    </span>
                    <input
                      type="checkbox"
                      checked={thanksEnabled}
                      onChange={(e) => {
                        setThanksEnabled(e.target.checked);
                        // 접힌 상태에서 켜면 설정 입력이 보이도록 펼친다
                        if (e.target.checked)
                          setCollapsed((prev) => ({ ...prev, thanks: false }));
                      }}
                      className="h-5 w-5 accent-indigo-600"
                    />
                  </label>
                </div>
                {!collapsed.thanks && (
                  <p className="mt-1 text-sm text-gray-500">
                    이 브랜드 상품을 보낼 때 감사글/상주명을 인쇄해 발송하는 경우
                    사용하세요.
                  </p>
                )}

                {!collapsed.thanks && thanksEnabled && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        안내 문구 (공통)
                      </label>
                      <textarea
                        value={thanksGuideText}
                        onChange={(e) => setThanksGuideText(e.target.value)}
                        placeholder="예: 위와 같은 형태로 인쇄되어 발송됩니다."
                        className="h-16 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>

                    {/* 인쇄 템플릿 목록 (구매자가 이 중 하나 선택) */}
                    {templates.map((t, idx) => {
                      const displayUrl = templateDisplayUrl(t);
                      return (
                        <div
                          key={t.key}
                          className={`rounded-lg border p-3 ${
                            t.enabled
                              ? 'border-gray-200'
                              : 'border-gray-200 bg-gray-50 opacity-70'
                          }`}
                        >
                          <div className="mb-2 flex items-center gap-2">
                            <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-bold text-indigo-700">
                              템플릿 {idx + 1}
                            </span>
                            <input
                              type="text"
                              value={t.name}
                              onChange={(e) =>
                                updateTemplate(t.key, { name: e.target.value })
                              }
                              placeholder="템플릿 이름 (예: 가로형)"
                              className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
                            />
                            <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={t.enabled}
                                onChange={(e) =>
                                  updateTemplate(t.key, { enabled: e.target.checked })
                                }
                                className="h-4 w-4 accent-indigo-600"
                              />
                              노출
                            </label>
                            <button
                              type="button"
                              onClick={() => removeTemplate(t.key)}
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-red-300 text-red-500 hover:bg-red-50"
                            >
                              ✕
                            </button>
                          </div>

                          <ImageUploadBox
                            title="인쇄 템플릿 이미지"
                            setFile={(f: File) => handleTemplateFile(t.key, f)}
                            defaultImageUrl={t.templateImage?.url}
                          />

                          {displayUrl ? (
                            <div className="mt-3">
                              <label className="mb-1 block text-sm font-medium text-gray-700">
                                인쇄 영역 지정
                              </label>
                              <p className="mb-2 text-xs text-gray-400">
                                <b>감사글 영역을 지정하지 않으면</b> 문구가 이미 인쇄된
                                카드로 동작합니다 — 구매자는 감사글 선택 없이{' '}
                                <b>보내는 분 이름만</b> 입력합니다.
                              </p>
                              <ThanksPrintBoxEditor
                                imageUrl={displayUrl}
                                messageBox={t.messageBox}
                                senderBox={t.senderBox}
                                onChange={(boxKey, box) =>
                                  handleBoxChange(t.key, boxKey, box)
                                }
                              />
                            </div>
                          ) : (
                            <p className="mt-3 rounded-lg bg-gray-50 px-3 py-4 text-center text-sm text-gray-400">
                              템플릿 이미지를 업로드하면 인쇄 영역을 지정할 수 있습니다.
                            </p>
                          )}

                          {/* 감사글 지정 문구 세트 (택1 강제) */}
                          <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium text-gray-700">
                                감사글 지정 문구 세트
                              </label>
                              <button
                                type="button"
                                onClick={() => addPreset(t.key)}
                                className="rounded-md border border-dashed border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                              >
                                + 문구 추가
                              </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              문구를 등록하면 구매자는 <b>이 중 하나만 선택</b>할 수 있고
                              자유입력이 막힙니다. 비워두면 기존처럼 자유입력이
                              허용됩니다. 줄바꿈·들여쓰기까지 그대로 인쇄됩니다.
                            </p>
                            {t.messagePresets.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {t.messagePresets.map((preset, pi) => (
                                  <div key={pi} className="flex items-start gap-2">
                                    <textarea
                                      value={preset}
                                      onChange={(e) =>
                                        updatePreset(t.key, pi, e.target.value)
                                      }
                                      placeholder={'예: 감사합니다.'}
                                      rows={2}
                                      className="min-h-[3rem] flex-1 whitespace-pre rounded-md border border-gray-300 px-2 py-1.5 font-mono text-sm"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removePreset(t.key, pi)}
                                      className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-red-300 text-red-500 hover:bg-red-50"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="mt-3 grid grid-cols-2 gap-3">
                              <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600">
                                  보내는 분 글자수 제한
                                </label>
                                <input
                                  type="number"
                                  value={t.senderNameMaxLength}
                                  onChange={(e) =>
                                    updateTemplate(t.key, {
                                      senderNameMaxLength: e.target.value,
                                    })
                                  }
                                  min={1}
                                  placeholder="예: 10 (비우면 제한 없음)"
                                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-400">
                                  인쇄되는 발신문구 <b>전체</b>(&quot;홍길동 배상&quot; =
                                  6자) 기준입니다. 예: 10으로 설정하면 이름은 최대 7자까지
                                  입력됩니다.
                                </p>
                              </div>
                              <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600">
                                  글자 크기 (이미지 세로 대비 %)
                                </label>
                                <input
                                  type="number"
                                  value={t.fontScale}
                                  onChange={(e) =>
                                    updateTemplate(t.key, { fontScale: e.target.value })
                                  }
                                  min={0.1}
                                  step={0.1}
                                  placeholder="예: 5 (비우면 기본 크기)"
                                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-400">
                                  감사글·발신문구 <b>공통</b> 크기입니다. 예: 5 = 이미지
                                  세로높이의 5%. 비우면 기본 크기 = 화면 폭의
                                  3.75%(10~19px) 로, 4:3 가로형 이미지 기준 세로의{' '}
                                  <b>약 4~6%</b> 수준입니다. 기본과 비슷하게 시작하려면
                                  5를 넣고 조절하세요. 미리보기와 인쇄에 동일하게
                                  적용됩니다.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      onClick={addTemplate}
                      className="w-full rounded-lg border border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                    >
                      + 템플릿 추가
                    </button>
                    {templates.length === 0 && (
                      <p className="text-center text-xs text-gray-400">
                        최소 1개의 템플릿을 추가하세요. 구매자가 이 중 하나를 선택합니다.
                      </p>
                    )}
                  </div>
                )}
              </section>

              <div className="sticky bottom-0 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
                <p className="text-xs text-gray-500">
                  {isSubmitting
                    ? '브랜드 정보를 저장하고 있습니다.'
                    : '변경한 내용은 저장 후 적용됩니다.'}
                </p>
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/gift-seller/brand')}
                    disabled={isSubmitting}
                    className="rounded border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 disabled:opacity-40"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                  >
                    {isSubmitting
                      ? '저장 중…'
                      : isEditMode
                      ? '변경사항 저장'
                      : '브랜드 등록'}
                  </button>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
};
